import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import DataTable from '../../components/DataTable';
import ConfirmDialog from '../../components/ConfirmDialog';
import { orgApi, rolesApi, usersApi } from '../../api/admin.api';
import { toApiError } from '../../api/client';
import { roleLabel } from '../../constants/roles';
import type { UserDto } from '../../types/admin';

interface UserFormValues {
  name: string;
  familyName: string;
  email: string;
  password?: string;
  roleId: number;
  unitId?: number | null;
  teamIds?: number[];
}

export default function Users() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<UserFormValues>();

  const [editing, setEditing] = useState<UserDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deactivating, setDeactivating] = useState<UserDto | null>(null);
  const [activating, setActivating] = useState<UserDto | null>(null);
  const [resetting, setResetting] = useState<UserDto | null>(null);
  const [resetForm] = Form.useForm<{ newPassword: string }>();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list });
  const unitsQuery = useQuery({ queryKey: ['units'], queryFn: orgApi.listUnits });
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: orgApi.listTeams });

  const unitName = useMemo(() => {
    const map = new Map((unitsQuery.data ?? []).map((u) => [u.id, u.name]));
    return (id: number | null) => (id != null ? (map.get(id) ?? `#${id}`) : '—');
  }, [unitsQuery.data]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'] });
  const onError = (err: unknown) => message.error(toApiError(err).message);

  const saveMutation = useMutation({
    mutationFn: (values: UserFormValues) => {
      if (editing) {
        return usersApi.update(editing.id, {
          name: values.name,
          familyName: values.familyName,
          roleId: values.roleId,
          unitId: values.unitId ?? null,
          teamIds: values.teamIds ?? [],
        });
      }
      return usersApi.create({
        name: values.name,
        familyName: values.familyName,
        email: values.email,
        password: values.password ?? '',
        roleId: values.roleId,
        unitId: values.unitId ?? null,
        teamIds: values.teamIds ?? [],
      });
    },
    onSuccess: () => {
      message.success(editing ? 'کاربر به‌روزرسانی شد' : 'کاربر ایجاد شد');
      setFormOpen(false);
      invalidate();
    },
    onError,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => usersApi.deactivate(id),
    onSuccess: () => {
      message.success('کاربر غیرفعال شد');
      setDeactivating(null);
      invalidate();
    },
    onError,
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => usersApi.activate(id),
    onSuccess: () => {
      message.success('کاربر فعال شد');
      setActivating(null);
      invalidate();
    },
    onError,
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) =>
      usersApi.resetPassword(id, { newPassword }),
    onSuccess: () => {
      message.success('رمز عبور بازنشانی شد');
      setResetting(null);
      resetForm.resetFields();
    },
    onError,
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setFormOpen(true);
  };

  const openEdit = (user: UserDto) => {
    setEditing(user);
    form.setFieldsValue({
      name: user.name,
      familyName: user.familyName,
      email: user.email,
      roleId: rolesQuery.data?.find((r) => r.name === user.role)?.id,
      unitId: user.unitId,
      teamIds: user.teamIds,
    });
    setFormOpen(true);
  };

  const openReset = (user: UserDto) => {
    resetForm.resetFields();
    setResetting(user);
  };

  const columns: ColumnsType<UserDto> = [
    { title: 'نام', key: 'name', render: (_, u) => `${u.name} ${u.familyName}`, sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'ایمیل', dataIndex: 'email', key: 'email' },
    { title: 'نقش', key: 'role', render: (_, u) => roleLabel(u.role) },
    { title: 'واحد', key: 'unit', render: (_, u) => unitName(u.unitId) },
    { title: 'تیم‌ها', key: 'teams', render: (_, u) => u.teamIds.length },
    {
      title: 'وضعیت',
      key: 'isActive',
      render: (_, u) => <Tag color={u.isActive ? 'green' : 'red'}>{u.isActive ? 'فعال' : 'غیرفعال'}</Tag>,
    },
    {
      title: 'آخرین ورود',
      key: 'lastLoginAt',
      render: (_, u) => (u.lastLoginAt ? dayjs(u.lastLoginAt).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, u) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(u)}>ویرایش</Button>
          <Button size="small" onClick={() => openReset(u)}>بازنشانی رمز</Button>
          {u.isActive ? (
            <Button size="small" danger onClick={() => setDeactivating(u)}>غیرفعال‌سازی</Button>
          ) : (
            <Button size="small" onClick={() => setActivating(u)}>فعال‌سازی</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<Typography.Title level={4} style={{ margin: 0 }}>مدیریت کاربران</Typography.Title>}
      extra={<Button type="primary" onClick={openCreate}>کاربر جدید</Button>}
    >
      <DataTable<UserDto>
        columns={columns}
        dataSource={usersQuery.data ?? []}
        loading={usersQuery.isLoading}
      />

      <Modal
        title={editing ? 'ویرایش کاربر' : 'کاربر جدید'}
        open={formOpen}
        onOk={() => form.submit()}
        onCancel={() => setFormOpen(false)}
        confirmLoading={saveMutation.isPending}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnHidden
      >
        <Form<UserFormValues> form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
          <Form.Item name="name" label="نام" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="familyName" label="نام خانوادگی" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="ایمیل" rules={[{ required: true, type: 'email' }]}>
            <Input dir="ltr" disabled={!!editing} />
          </Form.Item>
          {!editing && (
            <Form.Item name="password" label="رمز عبور" rules={[{ required: true, min: 8 }]}>
              <Input.Password dir="ltr" />
            </Form.Item>
          )}
          <Form.Item name="roleId" label="نقش" rules={[{ required: true }]}>
            <Select
              loading={rolesQuery.isLoading}
              options={(rolesQuery.data ?? []).map((r) => ({ value: r.id, label: roleLabel(r.name) }))}
            />
          </Form.Item>
          <Form.Item name="unitId" label="واحد">
            <Select
              allowClear
              loading={unitsQuery.isLoading}
              options={(unitsQuery.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
            />
          </Form.Item>
          <Form.Item name="teamIds" label="تیم‌ها">
            <Select
              mode="multiple"
              allowClear
              loading={teamsQuery.isLoading}
              options={(teamsQuery.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`بازنشانی رمز عبور: ${resetting?.email ?? ''}`}
        open={!!resetting}
        onOk={() => resetForm.submit()}
        onCancel={() => { resetForm.resetFields(); setResetting(null); }}
        confirmLoading={resetMutation.isPending}
        okText="بازنشانی"
        cancelText="انصراف"
        destroyOnHidden
      >
        <Form
          form={resetForm}
          layout="vertical"
          onFinish={({ newPassword }) => resetting && resetMutation.mutate({ id: resetting.id, newPassword })}
        >
          <Form.Item name="newPassword" label="رمز عبور جدید" rules={[{ required: true, min: 8 }]}>
            <Input.Password dir="ltr" />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmDialog
        open={!!deactivating}
        danger
        title="غیرفعال‌سازی کاربر"
        content={`آیا از غیرفعال‌سازی «${deactivating?.name} ${deactivating?.familyName}» مطمئن هستید؟ نشست‌های فعال او باطل می‌شوند.`}
        confirmLoading={deactivateMutation.isPending}
        onConfirm={() => deactivating && deactivateMutation.mutate(deactivating.id)}
        onCancel={() => setDeactivating(null)}
      />

      <ConfirmDialog
        open={!!activating}
        title="فعال‌سازی مجدد کاربر"
        content={`آیا می‌خواهید «${activating?.name} ${activating?.familyName}» را مجدداً فعال کنید؟ کاربر پس از آن می‌تواند با نقش و محدودهٔ دسترسی فعلی خود وارد سامانه شود.`}
        okText="فعال‌سازی"
        confirmLoading={activateMutation.isPending}
        onConfirm={() => activating && activateMutation.mutate(activating.id)}
        onCancel={() => setActivating(null)}
      />
    </Card>
  );
}
